import { CombinedGraphQLErrors, ErrorLike } from '@apollo/client';

/**
 * Error code that are returned by graphQL
 */
export enum GraphQLErrorCode {
  BadUserInput = 'BAD_USER_INPUT',
  BadRequest = 'BAD_REQUEST',
  InternalServerError = 'INTERNAL_SERVER_ERROR',
  Forbidden = 'FORBIDDEN',
  NotFound = 'NOT_FOUND',
  OfferExpired = 'OFFER_EXPIRED',
  /** The session moved to another company than the one the page sent in `x-expected-company-id`. */
  SessionCompanyChanged = 'SESSION_COMPANY_CHANGED',
}

/**
 * Apollo v4 unifies query/mutation errors under a single `error` property and removes the
 * `ApolloError` wrapper. GraphQL-shaped errors now arrive as `CombinedGraphQLErrors`, which
 * exposes the underlying GraphQL errors via `.errors`. We surface the first error's code so
 * call sites can branch on a known GraphQL business error (e.g. `OFFER_EXPIRED`).
 */
export function isGraphQLErrorCode(error: ErrorLike | undefined, errorCode: GraphQLErrorCode): boolean {
  if (!error) {
    return false;
  }

  if (!CombinedGraphQLErrors.is(error)) {
    return false;
  }

  return error.errors[0]?.extensions?.code === errorCode;
}

/**
 * Surface the human-readable message from a GraphQL business error so call sites can show the real
 * reason (e.g. a Stripe decline: "Card charge failed: Your card was declined.") instead of a generic
 * fallback. Returns undefined for network / non-GraphQL errors, where a generic message is safer.
 */
export function graphQLErrorMessage(error: ErrorLike | undefined): string | undefined {
  if (!error || !CombinedGraphQLErrors.is(error)) {
    return undefined;
  }

  return error.errors[0]?.message ?? undefined;
}
